"use client";

import NextImage from "next/image";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";

export interface ImageClientProps {
	src: string;
	alt: string;
	width?: number;
	height?: number;
	className?: string;
	loading: "lazy" | "eager";
	priority: boolean;
	fill?: boolean;
	fetchPriority?: "high" | "low" | "auto";
	style?: CSSProperties;
}

/**
 * Fades the image in after it finishes loading.
 *
 * @param props - Component props.
 * @returns The rendered image.
 */
export function ImageClient({ src, alt, width, height, className, loading, priority, fill, fetchPriority, style }: ImageClientProps) {
	const imageRef = useRef<HTMLSpanElement>(null);
	const [shouldLoad, setShouldLoad] = useState(priority);
	const [loaded, setLoaded] = useState(false);
	const mergedStyle = {
		...(!fill && width && height ? { aspectRatio: `${width} / ${height}` } : {}),
		...style,
	};

	useEffect(() => {
		if (priority || shouldLoad) {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "0px", threshold: 0 },
		);

		if (imageRef.current) {
			observer.observe(imageRef.current);
		}

		return () => observer.disconnect();
	}, [priority, shouldLoad]);

	if (!shouldLoad) {
		return <span ref={imageRef} className={cn(fill ? "absolute inset-0 block" : "block", className, "opacity-0")} style={mergedStyle} />;
	}

	return (
		<NextImage
			loading={loading}
			fetchPriority={fetchPriority}
			priority={priority}
			onLoad={() => setLoaded(true)}
			src={src}
			alt={alt}
			width={!fill ? width : undefined}
			height={!fill ? height : undefined}
			className={cn(className, loaded || priority ? "opacity-100" : "opacity-0")}
			fill={fill}
			style={style}
		/>
	);
}
